import { createWebHistory, createRouter } from "vue-router";
import { pinia } from "@/state/pinia";
import { useAuthStore } from "@/state/pinia";
import routes from "./routes";
import appConfig from "../app.config.json";
import { hasShopPermission } from "@/composables/useShopPermissions";
import { FINANCIALS_PERMISSION } from "@/composables/useFinancialVisibility";

let bootChecked = false;
let mustCreateWorkspace = false;
let lastTokenCheck = null;
const TOKEN_CHECK_INTERVAL = 60000; // Check token max once per minute

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

// Before each route evaluates...
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore(pinia);

  if (!auth.token && auth.loadToken) {
    await auth.loadToken();
  }

  const publicPrefixes = [
    "/login", "/logout",
    "/auth/start", "/auth/continue", "/auth/callback", "/auth/forgot", "/auth/reset",
    "/workspaces/new"
  ];
  const isPublic = publicPrefixes.some(p => to.path.startsWith(p));
  const token = auth.getToken && auth.getToken();

  if (token && from.path === "/workspaces/new" && to.path !== "/workspaces/new") {
    bootChecked = false;
    mustCreateWorkspace = false;
  }

  if (!isPublic && !token) {
    if (auth.isSsoEnabled()) {
      return auth.redirectToSso();
    }
    return next("/login");
  }

  // Check token validity: on initial page load, first boot, after interval, or when navigating from public routes
  const now = Date.now();
  const isInitialLoad = from.path === "/" && from.name === null; // User opened page or refreshed
  const shouldCheckToken = token && (
    isInitialLoad ||
    !bootChecked ||
    !lastTokenCheck ||
    (now - lastTokenCheck) > TOKEN_CHECK_INTERVAL ||
    (isPublic !== publicPrefixes.some(p => from.path.startsWith(p))) // transitioning from/to public routes
  );

  if (shouldCheckToken) {
    try {
      const base = process.env.VUE_APP_BE_URL || import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${base}api/me/status`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
      });
      lastTokenCheck = now;

      if (res.ok) {
        const data = await res.json();
        mustCreateWorkspace = !!data?.must_create_workspace;
        bootChecked = true;
      } else if (res.status === 401) {
        // Token is invalid or expired, logout and redirect to login
        auth.logout();
        bootChecked = false;
        mustCreateWorkspace = false;
        lastTokenCheck = null;
        return next("/login");
      } else if (res.status === 428) {
        // gdyby middleware od razu zablokował
        mustCreateWorkspace = true;
        bootChecked = true;
      }
    } catch (_) {
      // w razie błędu zostaw false — backend i tak wymusi 428
      lastTokenCheck = now;
      bootChecked = true;
    }
  }

  if (token && mustCreateWorkspace && to.path !== "/workspaces/new") {
    return next("/workspaces/new");
  }

  // Handle deep link ?shop=<uuid> (e.g. coming from LessCMS "Otwórz Commerce")
  // Resolve the shop's workspace from BE, activate both workspace + shop,
  // then continue to the cleaned URL.
  if (token && !mustCreateWorkspace && typeof to.query.shop === "string" && to.query.shop) {
    const targetShopUuid = to.query.shop;
    try {
      const base = process.env.VUE_APP_BE_URL || import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${base}api/shops/${encodeURIComponent(targetShopUuid)}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }
      });
      if (res.ok) {
        const shopData = (await res.json())?.data;
        if (shopData?.workspace_uuid && shopData?.uuid) {
          // Set workspace directly to avoid the side-effect in setActiveWorkspace()
          // that resets activeShop + triggers async fetchShops (would race with us).
          auth.activeWorkspace = shopData.workspace_uuid;
          localStorage.setItem("activeWorkspace", shopData.workspace_uuid);
          auth.setActiveShop(shopData.uuid);
        }
      }
    } catch (_) {
      // Ignore — fall through to normal routing (will land on /shops if no activeShop).
    }
    const cleanQuery = { ...to.query };
    delete cleanQuery.shop;
    return next({ path: to.path, query: cleanQuery, replace: true });
  }

  // If user has workspace but no active shop, redirect to /shops
  const shopExemptPaths = [
    "/workspaces", "/subscriptions", "/settings",
    "/users", "/user/settings", "/logout", "/shops",
  ];
  const isShopExempt = shopExemptPaths.some(p => to.path.startsWith(p)) || isPublic;

  if (token && !mustCreateWorkspace && !isShopExempt) {
    // Sklepy dojeżdżają asynchronicznie — bez czekania pierwsze przejście po
    // zalogowaniu widziało puste `activeShop` i odsyłało na listę, mimo że
    // sklep zaraz potem wybierał się sam.
    if (!auth.activeShop && auth.shopsReady) {
      try {
        await auth.shopsReady;
      } catch (e) {
        // Nie udało się pobrać — niżej zdecyduje zwykła ścieżka.
      }
    }

    // Lista sklepów jest workspace'owa. Zaproszony wprost do sklepu nie ma tam
    // czego oglądać (i tak zobaczyłby pustkę), więc go tam nie odsyłamy.
    if (!auth.activeShop && auth.hasActiveWorkspace()) {
      return next("/shops");
    }
  }

  // Price lists and coupons are financial modules end to end — their API
  // refuses roles without shop.financials.view, so a direct URL would land on
  // an empty screen full of 403 toasts. Bounce to the dashboard instead.
  // (Inventory reports handle this in-view with an explanatory notice.)
  if (token && auth.activeShop && to.meta?.requiresFinancials) {
    const allowed = await hasShopPermission(auth.activeShop, FINANCIALS_PERMISSION);
    if (!allowed) {
      return next("/");
    }
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            console.log("args" + args);
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
